import React, { useContext, useState } from "react";
import { companyUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import Tag from "../../profiles/Tag";
import DetailTile from "./detail_tile";
import { ProductContext } from "../data/product_context_management";

// Truncation threshold for the description body below, duplicated verbatim from AboutSection's own
// bio renderer (WET before DRY — this is the recipe's first duplication; extract a shared helper
// only once a third consumer needs it, per the codebase's own duplicate-twice-before-abstracting rule).
const DESCRIPTION_CHAR_LIMIT = 280;

// The view exposes product_made_from_label as its raw enum int and stays out of the wording; which
// noun a product uses for what it is made of is presentation, so the mapping lives here.
const MADE_FROM_LABELS = ["Ingredients", "Materials", "Made From"];

function madeFromLabel(value) {
  return MADE_FROM_LABELS[value] ?? MADE_FROM_LABELS[2];
}

// product_released_at is a date-only column: "2026-03-14" parses as UTC midnight, so format it in
// UTC or every viewer west of Greenwich reads the release a day early.
function formattedReleaseDate(value) {
  const options = { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, options);
}

// The three structured detail cards from the migrated product_* columns. Most migrated products
// carry none of these fields, so each card renders only when at least one of its tiles has
// something to say (occupancy derived in the provider) and the page collapses to whatever is left.
function OverviewCard() {
  const {
    product,
    productTags,
    showOverviewCard,
    showCompanyTile,
    showWebsiteTile,
    showPriceTile,
    showCategoryTile,
    showTagsRow,
    showDescription,
    viewerContext,
    setOpenEditModal,
  } = useContext(ProductContext);
  const [expanded, setExpanded] = useState(false);
  const editable = !!viewerContext?.editable;

  if (!showOverviewCard) return null;

  const hasTiles = showCompanyTile || showWebsiteTile || showPriceTile || showCategoryTile;
  const hasTilesOrTags = hasTiles || showTagsRow;

  // Count/slice by code point (not UTF-16 unit) so a boundary never splits an emoji surrogate pair,
  // matching AboutSection's own truncation behaviour exactly (see the module comment above).
  const descriptionChars = product.description ? Array.from(product.description) : [];
  const shouldTruncateDescription = descriptionChars.length > DESCRIPTION_CHAR_LIMIT;
  const collapsedDescription = `${descriptionChars
    .slice(0, DESCRIPTION_CHAR_LIMIT)
    .join("")
    .replace(/[ \t]+$/, "")}…`;

  return (
    <SectionCard
      id="product-section-overview"
      title="Overview"
      editable={editable}
      onEdit={() => setOpenEditModal("DESCRIPTION")}
      editButtonId="modal-button-description"
    >
      <div className="row">
        {showCompanyTile && (
          <DetailTile icon="fa-solid fa-building" label="Company">
            {product.company_slug ? (
              <a href={companyUrl({ slug: product.company_slug, migrate_id: product.company_migrate_id })}>
                {product.company_name}
              </a>
            ) : (
              product.company_name
            )}
          </DetailTile>
        )}

        {showWebsiteTile && (
          <DetailTile icon="fa-solid fa-globe" label="Website">
            <a href={product.product_url} target="_blank" rel="noopener noreferrer">
              {product.product_url}
            </a>
          </DetailTile>
        )}

        {showPriceTile && (
          <DetailTile icon="fa-solid fa-tag" label="Price">
            ${product.price}
          </DetailTile>
        )}

        {showCategoryTile && (
          <DetailTile icon="fa-solid fa-tags" label="Category">
            {product.product_category_names.join(", ")}
          </DetailTile>
        )}
      </div>

      {showTagsRow && (
        // The design separates the tags from the facts above them; with no facts there is nothing to
        // separate. 8 chips with no overflow control is what the design draws here — a person row
        // shows far fewer, so the number belongs to this card rather than to a shared setting.
        <div className={`d-flex flex-wrap align-items-center gap-1${hasTiles ? " border-top pt-3" : ""}`}>
          <span className="text-muted small text-uppercase me-1">Tags:</span>
          {productTags.slice(0, 8).map((tag) => (
            <Tag key={tag.hashtag_id}>{tag.hashtag_name}</Tag>
          ))}
        </div>
      )}

      {showDescription && (
        <div className={hasTilesOrTags ? "border-top pt-3 mt-3" : ""}>
          <div style={{ whiteSpace: "pre-wrap" }} className="text-break">
            {shouldTruncateDescription ? (
              <>
                {expanded ? product.description : collapsedDescription}{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline text-primary border-0"
                  style={{ font: "inherit" }}
                  aria-expanded={expanded}
                  onClick={() => setExpanded((prev) => !prev)}
                >
                  {expanded ? "see less" : "see more"}
                </button>
              </>
            ) : (
              product.description
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function RetailCard() {
  const { product, showRetailCard, showStoresTile, showSizesTile, showColorsTile } = useContext(ProductContext);

  if (!showRetailCard) return null;

  return (
    <SectionCard id="product-section-retail" title="Retail">
      <div className="row">
        {showStoresTile && (
          <DetailTile icon="fa-solid fa-store" label="Available at">
            {product.product_available_at_stores.join(", ")}
          </DetailTile>
        )}

        {showSizesTile && (
          <DetailTile icon="fa-solid fa-ruler" label="Sizes">
            {product.product_sizes.join(", ")}
          </DetailTile>
        )}

        {showColorsTile && (
          <DetailTile icon="fa-solid fa-palette" label="Colors">
            {product.product_colors.join(", ")}
          </DetailTile>
        )}
      </div>
    </SectionCard>
  );
}

function ManufacturingCard() {
  const { product, showManufacturingCard, showMadeFromTile, showCountryTile, showReleasedAtTile } =
    useContext(ProductContext);

  if (!showManufacturingCard) return null;

  return (
    <SectionCard id="product-section-manufacturing" title="Manufacturing">
      <div className="row">
        {showMadeFromTile && (
          <DetailTile icon="fa-solid fa-leaf" label={madeFromLabel(product.product_made_from_label)}>
            {product.product_made_from}
          </DetailTile>
        )}

        {showCountryTile && (
          <DetailTile icon="fa-solid fa-earth-americas" label="Manufactured in">
            {product.product_manufacturing_country_code}
          </DetailTile>
        )}

        {showReleasedAtTile && (
          <DetailTile icon="fa-solid fa-calendar-day" label="Release date">
            {formattedReleaseDate(product.product_released_at)}
          </DetailTile>
        )}
      </div>
    </SectionCard>
  );
}

export { OverviewCard, RetailCard, ManufacturingCard };
